/**
 * ScheduleGridDisplayControl.tsx
 * Compact toolbar cluster for the Machine Schedule grid's display preferences
 * (2026-07 sticky-headers UX pass): −/+ day-width zoom steps and the
 * compact-rows (one-line machine label) toggle. Pure controlled component —
 * state lives in useScheduleGridDisplay at the page level.
 *
 * 2026-07-24 follow-up: the S/M/L ToggleButtonGroup is gone — wrapping its
 * ToggleButtons in Tooltips silently disconnected them (ToggleButtonGroup
 * wires value/onChange into IMMEDIATE children via cloneElement — the same
 * trap as the ScenarioPhaseStepper TooltipTab), and the owner only wants −/+.
 */

import React from 'react';
import { Box, IconButton, ToggleButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import TableRowsIcon from '@mui/icons-material/TableRows';
import {
  DAY_COL_WIDTH_MAX,
  DAY_COL_WIDTH_MIN,
  DAY_COL_WIDTH_STEP,
} from '../../hooks/useScheduleGridDisplay';

interface ScheduleGridDisplayControlProps {
  dayColWidth: number;
  onZoom: (deltaPx: number) => void;
  compactRows: boolean;
  onCompactRowsChange: (compact: boolean) => void;
}

export const ScheduleGridDisplayControl: React.FC<ScheduleGridDisplayControlProps> = ({
  dayColWidth,
  onZoom,
  compactRows,
  onCompactRowsChange,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
    {/* Compact one-line rows */}
    <Tooltip title={compactRows ? 'Comfortable rows (two-line machine labels)' : 'Compact rows (one-line machine labels)'}>
      <ToggleButton
        value="compact"
        selected={compactRows}
        onChange={() => onCompactRowsChange(!compactRows)}
        size="small"
        aria-label="Toggle compact rows"
        sx={{ px: 0.75, mr: 0.75, borderRadius: '6px' }}
      >
        <TableRowsIcon sx={{ fontSize: 16 }} />
      </ToggleButton>
    </Tooltip>

    {/* Zoom out / in — Ctrl+wheel over the grid also zooms */}
    <Tooltip title="Narrower day columns (Ctrl+wheel over the grid also zooms)">
      <span>
        <IconButton
          size="small"
          aria-label="Zoom out"
          disabled={dayColWidth <= DAY_COL_WIDTH_MIN}
          onClick={() => onZoom(-DAY_COL_WIDTH_STEP)}
        >
          <RemoveIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </span>
    </Tooltip>
    <Typography
      variant="caption"
      sx={{ fontSize: '0.66rem', color: 'text.secondary', minWidth: 30, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
    >
      {dayColWidth}px
    </Typography>
    <Tooltip title="Wider day columns (Ctrl+wheel over the grid also zooms)">
      <span>
        <IconButton
          size="small"
          aria-label="Zoom in"
          disabled={dayColWidth >= DAY_COL_WIDTH_MAX}
          onClick={() => onZoom(DAY_COL_WIDTH_STEP)}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </span>
    </Tooltip>
  </Box>
);
